import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import { useBoundStore } from "useStore";
import { useShallow } from "zustand/react/shallow";

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isEdited",
})(({ isEdited }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
  backgroundColor: isEdited ? "#e4f2fe" : "white",
  padding: 0,
  border: "none",
}));

const StyledNumericFormat = styled(NumericFormat)({
  textAlign: "center",
  border: "none",
  outline: "none",
  background: "transparent",
  width: "100%",
  fontSize: "inherit",
  fontFamily: "inherit",
});

export default function EditCmNumberInput({
  id,
  field,
  hasFocus,
  row,
  prefix,
  suffix,
  thousandSeparator,
  decimalScale,
}) {
  const editCmField = useBoundStore((state) => state.editCmField);
  const clearCmFieldEdit = useBoundStore((state) => state.clearCmFieldEdit);
  const cmEditedRow = useBoundStore(
    useShallow((state) => state.cmEditedRows[id]),
  );

  const getRawValue = (val) => {
    if (val === null || val === undefined) return "";
    const extracted = typeof val === "object" ? val.value : val;
    return isNaN(Number(extracted)) ? "" : extracted;
  };

  const originalValue = getRawValue(row[field]);
  const fieldData = cmEditedRow?.[field];
  const currentValue =
    fieldData?.value !== undefined
      ? getRawValue(fieldData.value)
      : originalValue;

  const [localValue, setLocalValue] = useState(currentValue);

  useEffect(() => {
    setLocalValue(currentValue);
  }, [currentValue]);

  const handleValueChange = (values) => {
    setLocalValue(values.floatValue === undefined ? "" : values.floatValue);
  };

  const handleBlur = () => {
    if (localValue === "" || localValue === undefined) {
      clearCmFieldEdit(id, field, row);
      setLocalValue(originalValue);
    } else if (Number(localValue) !== Number(originalValue)) {
      editCmField(row, field, localValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  const isEdited = Boolean(cmEditedRow);

  return (
    <StyledBox isEdited={isEdited}>
      <StyledNumericFormat
        value={localValue === 0 ? 0 : localValue || ""}
        onValueChange={handleValueChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        prefix={prefix || ""}
        suffix={suffix || ""}
        thousandSeparator={thousandSeparator ?? false}
        decimalScale={decimalScale ?? 2}
        allowNegative={false}
        autoFocus={hasFocus}
      />
    </StyledBox>
  );
}
